import { Eye, MessageSquare, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InvoiceCommentsPeek } from "@/components/invoices/InvoiceCommentsPeek";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import { getActionButtonClass } from "@/lib/semanticColors";
import { cn } from "@/lib/utils";

const actionClass = "table-row-action rounded-[0.8rem] shadow-none";

/**
 * The row's actions as a single labelled menu.
 *
 * Four icon buttons need 140px, which is more than the options column is given
 * on a tablet and more than the phone card can spare beside a status badge —
 * they were the cell forcing horizontal scroll at 768px. They also carried their
 * only visible label in a `<Tooltip>`, and touch devices do not hover, so a
 * sighted operator on a tablet saw four unlabelled glyphs and had to guess.
 *
 * The menu solves both: one 44px target, and every action reads as words.
 */
function InvoiceRowActionsMenu({ invoice, onDelete, isDeleting, canEdit, canDelete }) {
  const commentCount = invoice.comments?.length ?? 0;

  return (
    <div className="flex items-center justify-end" onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              disabled={isDeleting}
              aria-label={`Actions for invoice ${invoice.invoiceNumber}`}
              className={cn(actionClass, getActionButtonClass("view"))}
            />
          }
        >
          <MoreVertical className="size-4" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52 p-1">
          <DropdownMenuItem
            render={<Link to={`/invoice/view/${invoice.id}`} />}
          >
            <Eye />
            View details
          </DropdownMenuItem>

          {/* TEMP: status lock removed, only the permission gates Edit for now.
              TODO: restore `canEdit && !getEditLockReason(invoice)` (import from
              "@/lib/invoiceActions"), or disable it with the reason as a tooltip. */}
          {canEdit && (
            <DropdownMenuItem render={<Link to={`/invoice/edit/${invoice.id}`} />}>
              <Pencil />
              Edit invoice
            </DropdownMenuItem>
          )}

          {commentCount > 0 && (
            <DropdownMenuItem
              render={
                <Link to={`/invoice/view/${invoice.id}`} state={{ tab: "comments" }} />
              }
            >
              <MessageSquare />
              {`Read ${commentCount} comment${commentCount === 1 ? "" : "s"}`}
            </DropdownMenuItem>
          )}

          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                disabled={isDeleting}
                onClick={() => onDelete?.(invoice)}
              >
                <Trash2 />
                Delete invoice
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function InvoiceRowActions({ invoice, onDelete, isDeleting = false, compact = false }) {
  const can = useHasPermission();
  const canEdit = can(PERMISSIONS.UPDATE_INVOICE);
  const canDelete = can(PERMISSIONS.DELETE_INVOICE);

  if (compact) {
    return (
      <InvoiceRowActionsMenu
        invoice={invoice}
        onDelete={onDelete}
        isDeleting={isDeleting}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {/* View leads: it is the safe, always-available action, and the one every
          row supports regardless of status or permission. */}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              nativeButton={false}
              render={<Link to={`/invoice/view/${invoice.id}`} />}
              aria-label={`View invoice ${invoice.invoiceNumber}`}
              className={cn(actionClass, getActionButtonClass("view"))}
            />
          }
        >
          <Eye className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>View details</TooltipContent>
      </Tooltip>

      {/* TEMP: status lock removed, only the permission gates Edit for now.
          TODO: restore `canEdit && !getEditLockReason(invoice)` (import from
          "@/lib/invoiceActions"), or disable it with the reason as a tooltip. */}
      {canEdit && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                nativeButton={false}
                render={<Link to={`/invoice/edit/${invoice.id}`} />}
                aria-label={`Edit invoice ${invoice.invoiceNumber}`}
                className={cn(actionClass, getActionButtonClass("edit"))}
              />
            }
          >
            <Pencil className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent>Edit invoice</TooltipContent>
        </Tooltip>
      )}

      {/* Renders nothing when the invoice has no comments — the icon is the
          signal that there is something to read. */}
      <InvoiceCommentsPeek invoice={invoice} className={actionClass} />

      {canDelete && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                disabled={isDeleting}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete?.(invoice);
                }}
                aria-label={`Delete invoice ${invoice.invoiceNumber}`}
                className={cn(actionClass, getActionButtonClass("delete"))}
              />
            }
          >
            <Trash2 className="size-3.5" />
          </TooltipTrigger>
          <TooltipContent>Delete invoice</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
